import React from "react";
import { Link } from "react-router-dom";
import { getAuth, isAdmin } from "../auth";
import { PermissionKeys } from "../permissions";
import { usePermission } from "./Auth/PermissionContext";

const roleLabels = {
  admin: "平台管理员",
  customeradmin: "客户管理员",
  operator: "操作员"
};

const quickActions = [
  {
    to: "/gps-monitor",
    title: "实时监控",
    description: "查看授权车辆实时位置、速度和报警状态。",
    permission: PermissionKeys.MonitorView,
    icon: "📍",
    accent: "primary"
  },
  {
    to: "/track-playback",
    title: "轨迹回放",
    description: "按车辆和时间回放历史行驶轨迹。",
    permission: PermissionKeys.TrackView,
    icon: "🛣️",
    accent: "info"
  },
  {
    to: "/alarm-logs",
    title: "报警日志",
    description: "查看和跟进授权范围内的车辆报警。",
    permission: PermissionKeys.AlarmLogView,
    icon: "🚨",
    accent: "danger"
  },
  {
    to: "/connection-logs",
    title: "连接日志",
    description: "排查设备上线、离线和连接异常。",
    permission: PermissionKeys.ConnectionLogView,
    icon: "🔌",
    accent: "success"
  },
  {
    to: "/devices",
    title: "设备管理",
    description: "维护设备基础信息、绑定关系和状态。",
    permission: PermissionKeys.DeviceView,
    roles: ["Admin"],
    icon: "🚗",
    accent: "secondary"
  },
  {
    to: "/groups",
    title: "车辆分组",
    description: "按客户或业务场景维护车辆分组。",
    permission: PermissionKeys.GroupView,
    roles: ["Admin", "CustomerAdmin"],
    icon: "🗂️",
    accent: "warning"
  },
  {
    to: "/users",
    title: "用户管理",
    description: "管理客户账号、角色和访问范围。",
    permission: PermissionKeys.UserView,
    roles: ["Admin", "CustomerAdmin"],
    icon: "👥",
    accent: "dark"
  },
  {
    to: "/system-operation-logs",
    title: "操作审计",
    description: "查看关键后台操作和审计记录。",
    permission: PermissionKeys.SystemOpLogView,
    roles: ["Admin", "CustomerAdmin"],
    icon: "📋",
    accent: "primary"
  },
  {
    to: "/ggg-manager",
    title: "GGG 管理",
    description: "查看服务、客户端、指令和固件相关能力。",
    permission: PermissionKeys.GggManagerView,
    roles: ["Admin"],
    icon: "🧭",
    accent: "info"
  }
];

function roleText(role) {
  return roleLabels[(role || "").toLowerCase()] || role || "用户";
}

function canShowAction(action, role, can) {
  const roleAllowed = !action.roles || action.roles.some((x) => x.toLowerCase() === (role || "").toLowerCase());
  return roleAllowed && can(action.permission);
}

export function Home() {
  const auth = getAuth() || {};
  const admin = isAdmin();
  const role = auth.role || "";
  const { can, initialized } = usePermission();
  const visibleActions = initialized
    ? quickActions.filter((action) => canShowAction(action, role, can))
    : [];
  const displayName = auth.realName || auth.loginName || "用户";
  const consoleTitle = admin ? "平台管理控制台" : "我的车辆监控台";
  const consoleDescription = admin
    ? "统一查看平台车辆、设备、报警、审计与运维管理入口。"
    : "查看授权车辆的实时状态、历史轨迹和报警信息。";
  const scopeText = admin
    ? "全平台数据与管理能力"
    : auth.customerId
      ? `所属客户 #${auth.customerId} 的授权数据`
      : "当前账号授权范围内的数据";
  const attentionItems = admin
    ? [
        "优先关注未处理报警、异常连接和长时间离线设备。",
        "涉及设备指令、权限调整和关键配置变更时，注意保留操作审计。",
        "上线或配置变更前，请确认数据库、Redis、API 和前端站点状态。"
      ]
    : [
        "有报警时请优先进入报警日志确认处理状态。",
        "车辆长时间离线时，请检查设备供电、SIM 卡和网络环境。",
        "需要查看车辆位置或轨迹时，请进入实时监控或轨迹回放。"
      ];

  return (
    <div className="home-dashboard">
      <section className="home-hero status-card">
        <div>
          <div className="home-eyebrow">MycGPS V2</div>
          <h2>欢迎回来，{displayName}</h2>
          <p className="home-hero-desc">{consoleTitle} · {consoleDescription}</p>
          <div className="home-hero-meta">
            <span>{roleText(role)}</span>
            <span>{scopeText}</span>
            <span>在线站点：http://gps.mycsz.com</span>
          </div>
        </div>
        <div className="home-hero-actions">
          {can(PermissionKeys.MonitorView) && <Link className="btn btn-primary" to="/gps-monitor">进入实时监控</Link>}
          {can(PermissionKeys.TrackView) && <Link className="btn btn-outline-primary" to="/track-playback">轨迹回放</Link>}
        </div>
      </section>

      <section className="home-summary-grid">
        <div className="home-summary-card status-card">
          <span className="home-summary-label">车辆范围</span>
          <strong>{admin ? "全平台" : "我的车辆"}</strong>
          <small>{admin ? "按权限查看客户与设备" : "仅显示授权设备"}</small>
        </div>
        <div className="home-summary-card status-card">
          <span className="home-summary-label">实时状态</span>
          <strong>实时监控</strong>
          <small>查看在线、速度、位置与报警</small>
        </div>
        <div className="home-summary-card status-card">
          <span className="home-summary-label">报警处理</span>
          <strong>{admin ? "平台报警" : "我的报警"}</strong>
          <small>进入报警日志查看处理状态</small>
        </div>
        <div className="home-summary-card status-card">
          <span className="home-summary-label">操作范围</span>
          <strong>{roleText(role)}</strong>
          <small>功能入口按当前权限展示</small>
        </div>
      </section>

      <div className="home-content-grid">
        <section className="status-card home-section">
          <div className="home-section-header">
            <div>
              <h4>常用功能</h4>
              <p>根据当前账号角色和权限显示可用入口。</p>
            </div>
          </div>

          {!initialized && <div className="status-banner info">正在加载功能权限...</div>}
          {initialized && visibleActions.length === 0 && (
            <div className="status-banner empty">当前账号暂无可展示的首页功能入口，请联系管理员确认权限。</div>
          )}
          <div className="home-action-grid">
            {visibleActions.map((action) => (
              <Link key={action.to} className={`home-action-card accent-${action.accent}`} to={action.to}>
                <span className="home-action-icon">{action.icon}</span>
                <span>
                  <strong>{action.title}</strong>
                  <small>{action.description}</small>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <aside className="status-card home-section home-side-panel">
          <h4>{admin ? "运维关注" : "待关注事项"}</h4>
          <ul className="home-check-list">
            {attentionItems.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <div className="status-banner info mb-0">
            {admin
              ? "统计数字后续可接入 Dashboard 汇总接口，目前首页先作为统一导航和操作提示入口。"
              : "首页仅展示当前账号授权范围内的业务入口，不显示平台级管理数据。"}
          </div>
        </aside>
      </div>
    </div>
  );
}
